import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AgencyContext = createContext(null);

const STORAGE_KEY = "byrd_active_agency_id";

export function AgencyProvider({ children }) {
  const { user } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rep-agencies");
      setAgencies(data);
      // Initial selection: stored value if still valid, else the default agency, else the first one
      const stored = localStorage.getItem(STORAGE_KEY);
      const storedValid = stored && data.some((a) => a.id === stored);
      if (!storedValid) {
        const def = data.find((a) => a.is_default) || data[0];
        if (def) {
          localStorage.setItem(STORAGE_KEY, def.id);
          setActiveId(def.id);
        }
      } else {
        setActiveId(stored);
      }
      return data;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only super-admin / editor see the AgencySwitcher; for agency_user
    // and distributor we skip the list call entirely.
    if (user && (user.role === "admin" || user.role === "editor")) {
      refresh();
    } else {
      setAgencies([]);
      setActiveId(null);
    }
  }, [user, refresh]);

  const switchAgency = (id) => {
    if (!id) return;
    localStorage.setItem(STORAGE_KEY, id);
    setActiveId(id);
  };

  const active = agencies.find((a) => a.id === activeId) || null;

  return (
    <AgencyContext.Provider
      value={{ agencies, active, activeId, switchAgency, refresh, loading }}
    >
      {children}
    </AgencyContext.Provider>
  );
}

export function useAgency() {
  const ctx = useContext(AgencyContext);
  if (!ctx) throw new Error("useAgency must be used within an AgencyProvider");
  return ctx;
}
